import {
    TBankStatementResolutionMethods,
    TOffersDataRequirements,
    UploadedFile
} from './types';
import { RootState } from '../../reducers';

export function fetchDocumentRequirements(state: RootState) {
    return state.tradingHistory?.selectedOffers?.data_requirements.filter(
        (requirement: TOffersDataRequirements[number]) =>
            requirement.category === 'documents'
    );
}

export function fetchUploadedDocuments(state: RootState) {
    return state.tradingHistory.uploadedFiles;
}

export const getIsUploadingFileByType = (
    state: RootState,
    type: string
): boolean => {
    return state.tradingHistory.uploadedFiles.some(
        (file: UploadedFile) => file.type === type && file.loading
    );
};

export const getHasUploadedDocumentByType = (
    state: RootState,
    type: string
): boolean => {
    return state.tradingHistory.uploadedFiles.some(
        (file: UploadedFile) => file.type === type
    );
};

export const getIsOptionalFileUploading = (state: RootState): boolean => {
    if (!state.tradingHistory.selectedOffers) return false;

    const dataRequirementCategory =
        state.tradingHistory.selectedOffers?.data_requirements.filter(
            (requirement: TOffersDataRequirements[number]) =>
                requirement.category === 'documents'
        );

    if (dataRequirementCategory.length < 1) return false;

    const optionalRequirements = dataRequirementCategory[0].actions.filter(
        (action: TOffersDataRequirements[number]['actions'][number]) =>
            action.is_optional
    );

    if (optionalRequirements.length < 1) return false;

    return optionalRequirements.some(
        (requirement: TOffersDataRequirements[number]['actions'][number]) => {
            if (!requirement.type) return;

            return getIsUploadingFileByType(state, requirement.type);
        }
    );
};

export const getIsFileUploading = (state: RootState) => {
    return state.tradingHistory.uploadedFiles.some(
        (file: UploadedFile) => file.loading === true
    );
};

export const hasDocumentUploadedButStillRequired = (
    state: RootState
): boolean => {
    const offersResponse = state.lendingAPI?.offers?.data?.offers;
    const uploadedFiles = state.tradingHistory?.uploadedFiles;

    if (!offersResponse || !offersResponse.length) return false;

    const documentRequirements = offersResponse[0].data_requirements.find(
        (req: TOffersDataRequirements[number]) => req.category === 'documents'
    );

    if (!documentRequirements) return false;

    const requiredDocs = documentRequirements.actions.filter(
        (action: TOffersDataRequirements[number]['actions'][number]) =>
            !action.is_optional
    );

    if (uploadedFiles.length === 0) {
        return false;
    }

    // required check due to inconsistent returns on document_type for upload files vs product offers api
    const uploadedDocTypes = uploadedFiles.map((file: UploadedFile) =>
        file.type === 'business_accounts' ? 'company_accounts' : file.type
    );

    // check if any uploaded required document is still marked as 'required' in the state (instead of 'pending')
    // while waiting for the /offers response to update it to 'pending'
    const anyUploadedDocStillRequired = requiredDocs.some(
        (doc: TOffersDataRequirements[number]['actions'][number]) => {
            const isUploaded = uploadedDocTypes.includes(doc.type);
            return isUploaded && doc.status === 'required';
        }
    );

    return anyUploadedDocStillRequired;
};

export const getTrueLayerLinkedBankAccounts = (
    state: RootState
): TBankStatementResolutionMethods | null => {
    if (!state.tradingHistory.selectedOffers) return null;

    const dataRequirementCategory =
        state.tradingHistory.selectedOffers?.data_requirements.filter(
            (requirement: TOffersDataRequirements[number]) => {
                return requirement.category === 'documents';
            }
        );

    if (dataRequirementCategory.length < 1) return null;

    const dataRequirementBankStatement =
        dataRequirementCategory[0].actions.filter(
            (action: TOffersDataRequirements[number]['actions'][number]) =>
                action.recommended_resolution_method ===
                    'upload_bank_statement' ||
                action.recommended_resolution_method ===
                    'kontoauszuege_hochladen'
        );

    if (!dataRequirementBankStatement) return null;

    const resolutionMethod =
        dataRequirementBankStatement[0].resolution_methods.filter(
            (method: Record<string, never>) => {
                return method.type === 'link_bank_account_via_open_banking';
            }
        )[0];

    if (!resolutionMethod || resolutionMethod.items.length < 1) return null;

    // Currently multiple accounts can exist in one bank account with the same name. (e.g. Wise, Lloyds savings & chequing)
    // TODO: Change to return list of items when BE adds a new property that differentiates bank accounts.
    // e.g last_four_digits: 1234
    return resolutionMethod.items[0];
};
